<!-- views/donnate.vue — Formulaire de don étape par étape -->
<template>
  <main class="sf-page dn-page">

    <!-- ── Page hero ──────────────────────────────────────── -->
    <div class="sf-page-hero">
      <div class="sf-page-hero__inner">
        <p class="sf-page-hero__eyebrow"><span class="sf-page-hero__eyebrow-dot"></span>Soutenez la mission</p>
        <h1 class="sf-h1">Faire un <em>don</em></h1>
        <p class="sf-page-hero__sub">Chaque contribution transforme une vie. Merci pour votre générosité.</p>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════════
         DON VIA PAYPAL — actif par défaut (usePaypalDonation = true)
    ═══════════════════════════════════════════════════════════ -->
    <section v-if="usePaypalDonation" class="sf-section sf-section--cream">
      <div class="sf-container">
        <div class="dn-paypal-wrap">

          <div class="dn-card dn-paypal-card" data-aos="fade-up">
            <h2 class="dn-card__title">Faire un don via PayPal</h2>
            <p class="dn-card__sub">Choisissez un montant (optionnel), puis finalisez votre don en toute sécurité sur PayPal.</p>

            <!-- Montants suggérés -->
            <div class="dn-amounts-grid">
              <button
                v-for="amt in paypalAmounts"
                :key="amt"
                class="dn-amount-btn"
                :class="{'dn-amount-btn--active': paypalAmount === amt && !paypalCustomAmount}"
                @click="selectPaypalAmount(amt)"
              >
                <span class="dn-amount-btn__val">{{ amt }} €</span>
              </button>
            </div>

            <!-- Montant personnalisé -->
            <div class="dn-custom-amount" :class="{'dn-custom-amount--active': !!paypalCustomAmount}">
              <label class="sf-input-label">Autre montant (€)</label>
              <div class="dn-custom-input-wrap">
                <span class="dn-custom-sym">€</span>
                <input
                  v-model.number="paypalCustomAmount"
                  type="number"
                  min="1"
                  step="1"
                  class="sf-input"
                  placeholder="Entrez votre montant"
                  @input="onPaypalCustomAmount"
                  @focus="paypalAmount = null"
                />
              </div>
            </div>

            <p class="dn-paypal-notice">
              <Lock /> Vous serez redirigé vers le site sécurisé PayPal pour finaliser votre don. Nous ne collectons ni ne stockons vos informations bancaires.
            </p>

            <button class="sf-btn sf-btn--terra dn-paypal-btn" @click="goToPaypal">
              Faire mon don sur PayPal <ExternalLink class="sf-btn__icon" />
            </button>

            <p class="dn-paypal-alt">
              Vous pouvez aussi vous rendre directement sur
              <a :href="paypalUrl" target="_blank" rel="noopener">paypal.me/stagfondation</a>.
            </p>
          </div>

          <!-- Impact -->
          <div class="dn-impact-card dn-paypal-impact">
            <h3>Impact de votre don</h3>
            <div class="dn-impact-rows">
              <div
                v-for="imp in impactItems"
                :key="imp.code"
                class="dn-impact-row"
                :class="{'dn-impact-row--highlight': paypalFinalAmount >= imp.eur}"
              >
                <span class="dn-impact-row__amt">{{ imp.label }}</span>
                <span>{{ imp.desc }}</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════════════════════════════════
         FORMULAIRE DE DON ACTUEL — inchangé, affiché si usePaypalDonation = false
    ═══════════════════════════════════════════════════════════ -->
    <template v-else>

    <!-- ── Loader initial ─────────────────────────────────── -->
    <div v-if="configLoading" class="sf-loader" style="padding:5rem"><span class="sf-loader__ring"></span></div>

    <!-- ── Dons désactivés ────────────────────────────────── -->
    <section v-else-if="!config.enabled" class="sf-section sf-section--cream">
      <div class="sf-container">
        <div class="dn-disabled">
          <div class="dn-disabled__icon"><PauseCircle /></div>
          <h2 class="sf-h2" style="margin-bottom:.75rem">Collecte temporairement suspendue</h2>
          <p>{{ config.message }}</p>
          <router-link to="/contact"><button class="sf-btn sf-btn--outline" style="margin-top:1.5rem">Nous contacter</button></router-link>
        </div>
      </div>
    </section>

    <!-- ── Formulaire onboarding ─────────────────────────── -->
    <section v-else class="sf-section sf-section--cream">
      <div class="sf-container">
        <div class="dn-layout">

          <!-- Colonne principale -->
          
        <div class="dn-main">
            <!-- ── Progress bar ──────────────────────── -->
            
            <div class="dn-progress">
              <div class="dn-progress__bar" :style="{width: progressPct + '%'}"></div>
            </div>
            <div class="dn-steps-labels">
              <span
                v-for="(s, i) in steps"
                :key="i"
                class="dn-step-label"
                :class="{
                  'dn-step-label--active': currentStep === i,
                  'dn-step-label--done':   currentStep > i
                }"
              >
                <span class="dn-step-label__num">{{ i + 1 }}</span>
                <span class="dn-step-label__txt">{{ s }}</span>
              </span>
            </div>
           
            

            <!-- ═══════════════════════════════════════
                 ÉTAPE 1 — Devise
            ═══════════════════════════════════════ -->
            <div v-if="currentStep === 0" class="dn-card" data-aos="fade-up">
              <h2 class="dn-card__title">Choisissez votre devise</h2>
              <p class="dn-card__sub">Tous les paiements sont sécurisés. Les conversions sont calculées automatiquement.</p>

              <div class="dn-currency-grid">
                <button
                  v-for="cur in config.currencies"
                  :key="cur.code"
                  class="dn-currency-btn"
                  :class="{'dn-currency-btn--active': form.currency_code === cur.code}"
                  @click="selectCurrency(cur)"
                >
                  <span class="dn-currency-btn__sym">{{ cur.symbol }}</span>
                  <span class="dn-currency-btn__code">{{ cur.code }}</span>
                  <span class="dn-currency-btn__name">{{ cur.name }}</span>
                  <span v-if="!cur.is_base" class="dn-currency-btn__rate">1 EUR = {{ fmtRate(cur) }} {{ cur.code }}</span>
                </button>
              </div>

              <div class="dn-nav">
                <span></span>
                <button class="sf-btn sf-btn--terra" :disabled="!form.currency_code" @click="nextStep">
                  Continuer <ArrowRight class="sf-btn__icon" />
                </button>
              </div>
            </div>

            <!-- ═══════════════════════════════════════
                 ÉTAPE 2 — Projet
            ═══════════════════════════════════════ -->
            <div v-if="currentStep === 1" class="dn-card" data-aos="fade-up">
              <h2 class="dn-card__title">À quoi dédier votre don ?</h2>
              <p class="dn-card__sub">Choisissez un projet ou contribuez au fonds général.</p>

              <div class="dn-project-list">
                <button
                  v-for="proj in config.projects"
                  :key="proj.id ?? 'general'"
                  class="dn-project-btn"
                  :class="{'dn-project-btn--active': form.project_id === proj.id}"
                  @click="form.project_id = proj.id; form.project_slug = proj.slug"
                >
                  <span class="dn-project-btn__title">{{ proj.title }}</span>
                  <span v-if="proj.category" class="dn-project-btn__cat">{{ proj.category }}</span>
                  <CheckCircle v-if="form.project_id === proj.id" class="dn-project-btn__check" />
                </button>
              </div>

              <div class="dn-nav">
                <button class="sf-btn sf-btn--outline" @click="prevStep"><ArrowLeft class="sf-btn__icon" /> Retour</button>
                <button class="sf-btn sf-btn--terra" :disabled="form.project_id === undefined" @click="nextStep">
                  Continuer <ArrowRight class="sf-btn__icon" />
                </button>
              </div>
            </div>

            <!-- ═══════════════════════════════════════
                 ÉTAPE 3 — Montant
            ═══════════════════════════════════════ -->
            <div v-if="currentStep === 2" class="dn-card" data-aos="fade-up">
              <h2 class="dn-card__title">Quel montant souhaitez-vous donner ?</h2>
              <p class="dn-card__sub">En {{ selectedCurrency.name }} ({{ selectedCurrency.symbol }})</p>

              <!-- Montants par défaut -->
              <div class="dn-amounts-grid">
                <button
                  v-for="amt in defaultAmounts"
                  :key="amt.id"
                  class="dn-amount-btn"
                  :class="{'dn-amount-btn--active': form.amount === amt.amount && !customMode}"
                  @click="selectAmount(amt.amount)"
                >
                  <span class="dn-amount-btn__val">{{ formatMoney(amt.amount) }}</span>
                  <span v-if="amt.label" class="dn-amount-btn__label">{{ amt.label }}</span>
                  <span v-if="!isCurrencyBase" class="dn-amount-btn__eur">≈ {{ formatEurEquiv(amt.amount) }}</span>
                </button>
              </div>

              <!-- Montant personnalisé -->
              <div class="dn-custom-amount" :class="{'dn-custom-amount--active': customMode}">
                <label class="sf-input-label">Autre montant ({{ selectedCurrency.symbol }})</label>
                <div class="dn-custom-input-wrap">
                  <span class="dn-custom-sym">{{ selectedCurrency.symbol }}</span>
                  <input
                    v-model.number="customAmount"
                    type="number"
                    :min="minAmount"
                    step="1"
                    class="sf-input"
                    placeholder="Entrez votre montant"
                    @input="onCustomAmount"
                    @focus="customMode = true"
                  />
                </div>
                <p v-if="customMode && customAmount > 0 && !isCurrencyBase" class="dn-eur-hint">
                  ≈ {{ formatEurEquiv(customAmount) }}
                </p>
              </div>

              <!-- Erreur montant -->
              <p v-if="amountError" class="dn-error">{{ amountError }}</p>

              <div class="dn-nav">
                <button class="sf-btn sf-btn--outline" @click="prevStep"><ArrowLeft class="sf-btn__icon" /> Retour</button>
                <button class="sf-btn sf-btn--terra" :disabled="!finalAmount || !!amountError" @click="validateAmount">
                  Continuer <ArrowRight class="sf-btn__icon" />
                </button>
              </div>
            </div>

            <!-- ═══════════════════════════════════════
                 ÉTAPE 4 — Informations personnelles
            ═══════════════════════════════════════ -->
            <div v-if="currentStep === 3" class="dn-card" data-aos="fade-up">
              <h2 class="dn-card__title">Vos informations</h2>
              <p class="dn-card__sub">Nécessaires pour votre reçu fiscal. Vous pouvez aussi donner anonymement.</p>

              <label class="dn-anon-toggle">
                <div class="dn-toggle" :class="{'dn-toggle--on': form.is_anonymous}" @click="form.is_anonymous = !form.is_anonymous">
                  <span class="dn-toggle__knob"></span>
                </div>
                <span>Don anonyme</span>
              </label>

              <div v-if="!form.is_anonymous" class="dn-personal-form">
                <div class="dn-form-row">
                  <div>
                    <label class="sf-input-label sf-input-label--req">Prénom</label>
                    <input v-model="form.donor_name_first" type="text" class="sf-input" placeholder="Jean" />
                  </div>
                  <div>
                    <label class="sf-input-label sf-input-label--req">Nom</label>
                    <input v-model="form.donor_name_last" type="text" class="sf-input" placeholder="Dupont" />
                  </div>
                </div>
                <div>
                  <label class="sf-input-label sf-input-label--req">Email <small>(pour le reçu fiscal)</small></label>
                  <input v-model="form.donor_email" type="email" class="sf-input" placeholder="jean@exemple.fr" />
                </div>
                <div>
                  <label class="sf-input-label">Téléphone</label>
                  <input v-model="form.donor_phone" type="tel" class="sf-input" placeholder="+237 6XX XX XX XX" />
                </div>
                <div>
                  <label class="sf-input-label">Message (optionnel)</label>
                  <textarea v-model="form.message" class="sf-input sf-textarea" rows="3" placeholder="Un mot d'encouragement…"></textarea>
                </div>
              </div>

              <p v-if="personalError" class="dn-error">{{ personalError }}</p>

              <div class="dn-nav">
                <button class="sf-btn sf-btn--outline" @click="prevStep"><ArrowLeft class="sf-btn__icon" /> Retour</button>
                <button class="sf-btn sf-btn--terra" @click="validatePersonal">
                  Continuer <ArrowRight class="sf-btn__icon" />
                </button>
              </div>
            </div>

            <!-- ═══════════════════════════════════════
                 ÉTAPE 5 — Mode de paiement
            ═══════════════════════════════════════ -->
            <div v-if="currentStep === 4" class="dn-card" data-aos="fade-up">
              <h2 class="dn-card__title">Comment souhaitez-vous payer ?</h2>

              <div class="dn-payment-opts">
                <button
                  v-if="config.stripe_enabled"
                  class="dn-pay-opt"
                  :class="{'dn-pay-opt--active': form.payment_method === 'card'}"
                  @click="selectPaymentMethod('card')"
                >
                  <div class="dn-pay-opt__icon"><CreditCard /></div>
                  <div class="dn-pay-opt__info">
                    <p>Carte bancaire</p>
                    <small>Visa, Mastercard — Paiement sécurisé Stripe</small>
                  </div>
                  <CheckCircle v-if="form.payment_method === 'card'" class="dn-pay-opt__check" />
                </button>

                <button
                  v-if="config.mobile_money_enabled"
                  class="dn-pay-opt"
                  :class="{'dn-pay-opt--active': form.payment_method === 'mobile_money'}"
                  @click="selectPaymentMethod('mobile_money')"
                >
                  <div class="dn-pay-opt__icon"><Smartphone /></div>
                  <div class="dn-pay-opt__info">
                    <p>Mobile Money</p>
                    <small>Orange Money, MTN, Moov</small>
                  </div>
                  <CheckCircle v-if="form.payment_method === 'mobile_money'" class="dn-pay-opt__check" />
                </button>
              </div>

              <!-- ── Formulaire Stripe ─────────────────── -->
              <div v-if="form.payment_method === 'card'" class="dn-payment-form">
                <h3 class="dn-payment-form__title">Informations de carte</h3>
                <p class="dn-payment-form__notice">
                  <Lock /> Paiement sécurisé par Stripe. Nous ne stockons jamais vos données bancaires.
                </p>
                <!-- Stripe Elements sera monté ici après initialisation -->
                <div id="stripe-card-element" class="dn-stripe-element">
                  <div class="dn-stripe-placeholder">
                    <CreditCard />
                    <span>Les champs de carte Stripe seront affichés ici après intégration de la clé Stripe.</span>
                  </div>
                </div>
              </div>

              <!-- ── Formulaire Mobile Money ───────────── -->
              <div v-if="form.payment_method === 'mobile_money'" class="dn-payment-form">
                <h3 class="dn-payment-form__title">Paiement Mobile Money</h3>
                <div>
                  <label class="sf-input-label sf-input-label--req">Numéro Mobile Money</label>
                  <input v-model="mobileNumber" type="tel" class="sf-input" placeholder="+237 6XX XX XX XX" />
                  <p class="dn-hint">Vous recevrez une demande de paiement sur ce numéro.</p>
                </div>
                <div class="dn-operator-grid">
                  <button v-for="op in mobileOperators" :key="op.id" class="dn-operator-btn"
                    :class="{'dn-operator-btn--active': mobileOperator === op.id}"
                    @click="mobileOperator = op.id"
                  >{{ op.label }}</button>
                </div>
              </div>

              <div class="dn-nav">
                <button class="sf-btn sf-btn--outline" @click="prevStep"><ArrowLeft class="sf-btn__icon" /> Retour</button>
                <button
                  class="sf-btn sf-btn--terra"
                  :disabled="!form.payment_method || submitting"
                  @click="submitDonation"
                >
                  <span v-if="submitting" class="sf-loader__ring" style="width:18px;height:18px;border-width:2px;margin-right:.5rem"></span>
                  Donner {{ formatMoney(finalAmount) }} <ArrowRight class="sf-btn__icon" />
                </button>
              </div>
            </div>

            <!-- ═══════════════════════════════════════
                 ÉTAPE 6 — Confirmation
            ═══════════════════════════════════════ -->
            <div v-if="currentStep === 5" class="dn-card dn-confirm" data-aos="fade-up">
              <div class="dn-confirm__icon"><CheckCircle /></div>
              <h2 class="dn-card__title">Merci pour votre générosité !</h2>
              <p class="dn-card__sub">Votre don a été initialisé avec succès.</p>

              <div class="dn-confirm__details">
                <div class="dn-confirm-row"><span>Référence</span><strong>{{ confirmData.invoice_number }}</strong></div>
                <div class="dn-confirm-row"><span>Montant</span><strong>{{ formatMoney(confirmData.amount) }} (≈ {{ fmtEur(confirmData.amount_eur) }})</strong></div>
                <div class="dn-confirm-row"><span>Projet</span><strong>{{ projectLabel }}</strong></div>
                <div class="dn-confirm-row"><span>Statut</span><strong class="dn-status">En attente de paiement</strong></div>
              </div>

              <div v-if="form.payment_method === 'mobile_money' && confirmData.mobile_pay" class="dn-mobile-wait">
                <Smartphone />
                <p>Une demande de paiement a été envoyée au <strong>{{ mobileNumber }}</strong>. Confirmez-la sur votre téléphone.</p>
              </div>

              <p v-if="!form.is_anonymous && form.donor_email" class="dn-confirm__email">
                Un email de confirmation sera envoyé à <strong>{{ form.donor_email }}</strong>.
              </p>

              <div style="display:flex;gap:1rem;justify-content:center;margin-top:2rem;flex-wrap:wrap">
                <router-link to="/"><button class="sf-btn sf-btn--outline">Retour à l'accueil</button></router-link>
                <button class="sf-btn sf-btn--terra" @click="resetForm">Faire un autre don</button>
              </div>
            </div>

          </div><!-- /dn-main -->

          <!-- ── Résumé sidebar ──────────────────────────── -->
          <aside class="dn-sidebar" v-if="currentStep < 5">
            <div class="dn-summary">
              <h3 class="dn-summary__title">Résumé</h3>

              <div class="dn-summary__rows">
                <div class="dn-summary-row" v-if="selectedCurrency">
                  <span>Devise</span>
                  <strong>{{ selectedCurrency.code }} — {{ selectedCurrency.name }}</strong>
                </div>
                <div class="dn-summary-row" v-if="finalAmount">
                  <span>Montant</span>
                  <strong>{{ formatMoney(finalAmount) }}</strong>
                </div>
                <div class="dn-summary-row" v-if="finalAmount && !isCurrencyBase">
                  <span>Équivalent EUR</span>
                  <strong>{{ formatEurEquiv(finalAmount) }}</strong>
                </div>
                <div class="dn-summary-row" v-if="projectLabel">
                  <span>Projet</span>
                  <strong>{{ projectLabel }}</strong>
                </div>
                <div class="dn-summary-row" v-if="form.payment_method">
                  <span>Paiement</span>
                  <strong>{{ form.payment_method === 'card' ? 'Carte bancaire' : 'Mobile Money' }}</strong>
                </div>
              </div>

              <div v-if="finalAmount" class="dn-summary__fiscal">
                <Lock />
                <p>Après déduction fiscale à 66%, votre don vous coûte <strong>{{ fiscalCost }}</strong></p>
              </div>
            </div>

            <!-- Impact -->
            <div class="dn-impact-card">
              <h3>Impact de votre don</h3>
              <div class="dn-impact-rows">
                <div v-for="imp in impactItems" :key="imp.code" class="dn-impact-row" :class="{'dn-impact-row--highlight': isRelevantImpact(imp.eur)}">
                  <span class="dn-impact-row__amt">{{ imp.label }}</span>
                  <span>{{ imp.desc }}</span>
                </div>
              </div>
            </div>
          </aside>

        </div>
      </div>
    </section>

    </template>

    <!-- Toast -->
    <Transition name="toast"><div v-if="toastMsg" class="sf-toast"><CheckCircle />{{ toastMsg }}</div></Transition>
  </main>
</template>

<script>
import AOS from 'aos'; import 'aos/dist/aos.css'
import axios from 'axios'
import { ArrowRight, ArrowLeft, CheckCircle, CreditCard, Smartphone, Lock, PauseCircle, ExternalLink } from 'lucide-vue-next'

const LS_KEY = 'sf_donor_info'
const API    = (window.__API_URL__ || 'https://api.stagfondation.org/api') + '/public'

export default {
  name: 'DonatePage',
  components: { ArrowRight, ArrowLeft, CheckCircle, CreditCard, Smartphone, Lock, PauseCircle, ExternalLink },

  data() {
    return {
      // ── Bascule don PayPal ────────────────────────────────
      // true  : affiche le nouveau bloc de don via paypal.me/stagfondation
      // false : réaffiche le formulaire multi-étapes + logique de don actuels (inchangés)
      usePaypalDonation: true,
      paypalUrl:         'https://paypal.me/stagfondation',
      paypalAmounts:     [10, 25, 50, 100],
      paypalAmount:      null,
      paypalCustomAmount: null,

      configLoading: true,
      submitting:    false,
      toastMsg:      '',

      config: { enabled: true, currencies: [], projects: [], stripe_enabled: true, mobile_money_enabled: true, min_amount_eur: 5 },

      steps: ['Devise','Projet','Montant','Identité','Paiement'],
      currentStep: 0,

      selectedCurrency: null,
      defaultAmounts:   [],
      customMode:       false,
      customAmount:     null,
      amountError:      '',
      personalError:    '',

      mobileNumber:   '',
      mobileOperator: null,
      mobileOperators: [
        { id: 'orange', label: 'Orange Money' },
        { id: 'mtn',    label: 'MTN MoMo'     },
        { id: 'moov',   label: 'Moov Money'   },
      ],

      form: {
        currency_code:    'EUR',
        project_id:       null,
        project_slug:     'general',
        amount:           null,
        is_anonymous:     false,
        donor_name_first: '',
        donor_name_last:  '',
        donor_email:      '',
        donor_phone:      '',
        message:          '',
        payment_method:   null,
      },

      confirmData: {},

      impactItems: [
        { eur: 10,  label: '10 €',  desc: '1 semaine de formation',  code: 'i1' },
        { eur: 25,  label: '25 €',  desc: 'Équipement formateur',    code: 'i2' },
        { eur: 50,  label: '50 €',  desc: '1 mois de connexion',     code: 'i3' },
        { eur: 100, label: '100 €', desc: 'Installation antenne',    code: 'i4' },
      ],
    }
  },

  computed: {
    // ── PayPal ──────────────────────────────────────────────
    paypalFinalAmount() {
      return this.paypalCustomAmount || this.paypalAmount
    },
    paypalLink() {
      const amt = this.paypalFinalAmount
      return amt ? `${this.paypalUrl}/${amt}` : this.paypalUrl
    },

    progressPct() {
      return ((this.currentStep) / this.steps.length) * 100
    },
    finalAmount() {
      return this.customMode ? this.customAmount : this.form.amount
    },
    isCurrencyBase() {
      return this.selectedCurrency?.is_base
    },
    minAmount() {
      if (!this.selectedCurrency || this.selectedCurrency.is_base) return this.config.min_amount_eur
      if (this.selectedCurrency.rate_to_eur === 0) return 1
      return Math.ceil(this.config.min_amount_eur / this.selectedCurrency.rate_to_eur)
    },
    projectLabel() {
      const p = this.config.projects?.find(x => x.id === this.form.project_id)
      return p ? p.title : 'Fonds général'
    },
    fiscalCost() {
      if (!this.finalAmount) return '—'
      const eur = this.toEur(this.finalAmount)
      const net = eur * 0.34
      if (this.isCurrencyBase) return `${net.toFixed(2)} €`
      return `≈ ${net.toFixed(2)} €`
    },
  },

  async created() {
    if (this.usePaypalDonation) return // don via PayPal : pas besoin de charger la config de l'ancien flux
    await this.loadConfig()
    this.loadSavedInfo()
    this.configLoading = false
  },

  mounted() {
    AOS.init({ once: true, offset: 70, duration: 600 })
  },

  methods: {
    // ── PayPal ────────────────────────────────────────────────
    selectPaypalAmount(val) {
      this.paypalAmount       = val
      this.paypalCustomAmount = null
    },

    onPaypalCustomAmount() {
      this.paypalAmount = null
    },

    goToPaypal() {
      window.open(this.paypalLink, '_blank', 'noopener')
    },

    // ── Config ────────────────────────────────────────────────
    async loadConfig() {
      try {
        const { data } = await axios.get(`${API}/donations/config`)
        if (!data.enabled) {
          this.config = { enabled: false, message: data.message }
          return
        }
        this.config = data
        // sélectionner EUR par défaut
        const eur = data.currencies?.find(c => c.code === 'EUR') || data.currencies?.[0]
        if (eur) this.selectCurrency(eur)
      } catch (e) {
        this.config = { enabled: false, message: 'Service temporairement indisponible.' }
      }
    },

    selectCurrency(cur) {
      this.selectedCurrency      = cur
      this.form.currency_code    = cur.code
      this.defaultAmounts        = cur.amounts || []
      this.form.amount           = null
      this.customMode            = false
      this.customAmount          = null
    },

    // ── Navigation étapes ─────────────────────────────────────
    nextStep() { if (this.currentStep < this.steps.length) this.currentStep++ },
    prevStep() { if (this.currentStep > 0) this.currentStep-- },

    // ── Montant ───────────────────────────────────────────────
    selectAmount(val) {
      this.form.amount  = val
      this.customMode   = false
      this.customAmount = null
      this.amountError  = ''
    },

    onCustomAmount() {
      this.form.amount = null
      this.amountError = ''
    },

    validateAmount() {
      const amt = this.finalAmount
      if (!amt || amt <= 0) { this.amountError = 'Veuillez entrer un montant.'; return }
      if (amt < this.minAmount) { this.amountError = `Le montant minimum est ${this.formatMoney(this.minAmount)}.`; return }
      if (!this.isCurrencyBase) {
        const eurEquiv = this.toEur(amt)
        if (eurEquiv < this.config.min_amount_eur) {
          this.amountError = `Le montant minimum est l'équivalent de ${this.config.min_amount_eur} €.`; return
        }
      }
      if (this.customMode) this.form.amount = this.customAmount
      this.nextStep()
    },

    // ── Infos personnelles ────────────────────────────────────
    validatePersonal() {
      this.personalError = ''
      if (!this.form.is_anonymous) {
        if (!this.form.donor_name_first || !this.form.donor_name_last) {
          this.personalError = 'Veuillez entrer votre prénom et nom.'; return
        }
        if (!this.form.donor_email || !/\S+@\S+\.\S+/.test(this.form.donor_email)) {
          this.personalError = 'Veuillez entrer un email valide.'; return
        }
      }
      // Sauvegarder en localStorage
      if (!this.form.is_anonymous) {
        localStorage.setItem(LS_KEY, JSON.stringify({
          first: this.form.donor_name_first,
          last:  this.form.donor_name_last,
          email: this.form.donor_email,
          phone: this.form.donor_phone,
        }))
      }
      this.nextStep()
    },

    loadSavedInfo() {
      try {
        const saved = JSON.parse(localStorage.getItem(LS_KEY) || 'null')
        if (saved) {
          this.form.donor_name_first = saved.first || ''
          this.form.donor_name_last  = saved.last  || ''
          this.form.donor_email      = saved.email || ''
          this.form.donor_phone      = saved.phone || ''
        }
      } catch {}
    },

    // ── Paiement ──────────────────────────────────────────────
    selectPaymentMethod(method) {
      this.form.payment_method = method
    },

    async submitDonation() {
      this.submitting = true
      try {
        const payload = {
          amount:         this.finalAmount,
          currency_code:  this.form.currency_code,
          project_id:     this.form.project_id || undefined,
          payment_method: this.form.payment_method,
          is_anonymous:   this.form.is_anonymous,
          donor_name:     this.form.is_anonymous ? undefined : `${this.form.donor_name_first} ${this.form.donor_name_last}`.trim(),
          donor_email:    this.form.is_anonymous ? undefined : this.form.donor_email,
          donor_phone:    this.form.is_anonymous ? undefined : this.form.donor_phone,
          message:        this.form.message || undefined,
        }

        const { data } = await axios.post(`${API}/donations`, payload)

        if (data.success) {
          this.confirmData = data.data.donation
          this.confirmData.mobile_pay = data.data.mobile_pay

          // Si Stripe : ici on déclencherait Stripe.confirmCardPayment(data.data.client_secret)
          // Pour Mobile Money : afficher les instructions

          this.currentStep = 5
        }
      } catch (err) {
        this.toastMsg = err.response?.data?.message || 'Une erreur est survenue.'
        setTimeout(() => { this.toastMsg = '' }, 4000)
      } finally {
        this.submitting = false
      }
    },

    resetForm() {
      this.currentStep     = 0
      this.form            = { currency_code:'EUR', project_id:null, project_slug:'general', amount:null, is_anonymous:false, donor_name_first:'', donor_name_last:'', donor_email:'', donor_phone:'', message:'', payment_method:null }
      this.customMode      = false
      this.customAmount    = null
      this.confirmData     = {}
      const eur = this.config.currencies?.find(c => c.code === 'EUR') || this.config.currencies?.[0]
      if (eur) this.selectCurrency(eur)
      this.loadSavedInfo()
    },

    // ── Formatters ────────────────────────────────────────────
    toEur(amount) {
      if (!this.selectedCurrency) return amount
      return amount * (this.selectedCurrency.rate_to_eur || 1)
    },

    formatMoney(val) {
      if (!val) return '—'
      const sym = this.selectedCurrency?.symbol || '€'
      return `${Number(val).toLocaleString('fr-FR')} ${sym}`
    },

    formatEurEquiv(val) {
      const eur = this.toEur(val)
      return `${eur.toFixed(2)} €`
    },

    fmtEur(val) { return `${Number(val||0).toFixed(2)} €` },

    fmtRate(cur) {
      if (!cur.rate_to_eur || cur.rate_to_eur === 0) return '—'
      return Math.round(1 / cur.rate_to_eur).toLocaleString('fr-FR')
    },

    isRelevantImpact(eur) {
      const myEur = this.toEur(this.finalAmount || 0)
      return myEur >= eur
    },
  }
}
</script>

<style>@import './sf-design.css';</style>
<style scoped>
.dn-page {}

/* ── Layout ─────────────────────────────────────────────── */
.dn-layout{display:grid;grid-template-columns:1fr 320px;gap:2.5rem;align-items:start}
@media(max-width:900px){.dn-layout{grid-template-columns:1fr}}

/* ── Progress ────────────────────────────────────────────── */
.dn-progress{height:4px;background:var(--cream-d);border-radius:2px;overflow:hidden;margin-bottom:1rem}
.dn-progress__bar{height:100%;background:var(--terra);border-radius:2px;transition:width .5s ease}
.dn-steps-labels{display:flex;gap:0;margin-bottom:2rem;flex-wrap:wrap; padding-bottom:.25rem}
.dn-step-label{display:flex;align-items:center;gap:.35rem;font-size:.75rem;color:var(--ink-60);white-space:nowrap;padding:.25rem .5rem}
.dn-step-label__num{width:20px;height:20px;border-radius:50%;background:var(--cream-d);color:var(--ink-60);display:flex;align-items:center;justify-content:center;font-size:.7rem;font-weight:600;transition:all .3s ease}
.dn-step-label--active .dn-step-label__num{background:var(--terra);color:var(--white)}
.dn-step-label--done .dn-step-label__num{background:var(--forest);color:var(--white)}
.dn-step-label--active .dn-step-label__txt{color:var(--terra);font-weight:500}

/* ── Card ────────────────────────────────────────────────── */
.dn-card{background:var(--white);border-radius:10px;padding:2.5rem 2rem;box-shadow:var(--shadow-sm)}
.dn-card__title{font-family:var(--fd);font-size:1.6rem;font-weight:600;color:var(--ink);margin-bottom:.5rem}
.dn-card__sub{font-size:.88rem;color:var(--ink-60);line-height:1.6;margin-bottom:1.75rem}

/* ── Navigation ──────────────────────────────────────────── */
.dn-nav{display:flex ;align-items:center;justify-content:space-between;margin-top:2rem;padding-top:1.5rem;border-top:1px solid var(--ink-12)}

/* ── Devise ──────────────────────────────────────────────── */
.dn-currency-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:.75rem;margin-bottom:1.5rem}
.dn-currency-btn{background:var(--white);border:1.5px solid var(--ink-12);border-radius:8px;padding:1rem;text-align:center;cursor:pointer;transition:all .2s ease;display:flex;flex-direction:column;gap:.2rem}
.dn-currency-btn:hover{border-color:var(--terra)}
.dn-currency-btn--active{border-color:var(--terra);background:rgba(200,90,42,.05)}
.dn-currency-btn__sym{font-family:var(--fd);font-size:1.8rem;font-weight:700;color:var(--terra)}
.dn-currency-btn__code{font-size:.85rem;font-weight:600;color:var(--ink)}
.dn-currency-btn__name{font-size:.75rem;color:var(--ink-60)}
.dn-currency-btn__rate{font-size:.7rem;color:var(--sage);margin-top:.15rem}

/* ── Projet ──────────────────────────────────────────────── */
.dn-project-list{display:flex;flex-direction:column;gap:.5rem;margin-bottom:1.5rem;max-height:340px;overflow-y:auto}
.dn-project-btn{display:flex;align-items:center;gap:.75rem;padding:.9rem 1.1rem;background:var(--white);border:1.5px solid var(--ink-12);border-radius:6px;cursor:pointer;text-align:left;transition:all .2s ease}
.dn-project-btn:hover{border-color:var(--terra)}
.dn-project-btn--active{border-color:var(--terra);background:rgba(200,90,42,.05)}
.dn-project-btn__title{font-size:.9rem;font-weight:500;color:var(--ink);flex:1}
.dn-project-btn__cat{font-size:.72rem;color:var(--ink-60);background:var(--cream-d);padding:.2rem .5rem;border-radius:2px}
.dn-project-btn__check{width:18px;height:18px;color:var(--terra);flex-shrink:0}

/* ── Montants ────────────────────────────────────────────── */
.dn-amounts-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(130px,1fr));gap:.65rem;margin-bottom:1.25rem}
.dn-amount-btn{background:var(--white);border:1.5px solid var(--ink-12);border-radius:8px;padding:.9rem .75rem;text-align:center;cursor:pointer;transition:all .2s ease;display:flex;flex-direction:column;gap:.2rem}
.dn-amount-btn:hover{border-color:var(--terra)}
.dn-amount-btn--active{border-color:var(--terra);background:rgba(200,90,42,.05)}
.dn-amount-btn__val{font-family:var(--fd);font-size:1.4rem;font-weight:700;color:var(--ink)}
.dn-amount-btn__label{font-size:.72rem;color:var(--ink-60)}
.dn-amount-btn__eur{font-size:.7rem;color:var(--sage);margin-top:.1rem}

.dn-custom-amount{border:1.5px solid var(--ink-12);border-radius:8px;padding:1rem;transition:border-color .2s ease}
.dn-custom-amount--active{border-color:var(--terra)}
.dn-custom-input-wrap{display:flex;align-items:center;gap:.5rem}
.dn-custom-sym{font-family:var(--fd);font-size:1.3rem;font-weight:700;color:var(--terra)}
.dn-eur-hint{font-size:.78rem;color:var(--sage);margin-top:.35rem}
.dn-error{font-size:.82rem;color:var(--terra);margin-top:.75rem}
.dn-hint{font-size:.78rem;color:var(--ink-60);margin-top:.35rem}

/* ── Infos perso ─────────────────────────────────────────── */
.dn-anon-toggle{display:flex;align-items:center;gap:.75rem;cursor:pointer;margin-bottom:1.5rem;font-size:.9rem;color:var(--ink)}
.dn-toggle{width:44px;height:24px;border-radius:12px;background:var(--ink-12);position:relative;transition:background .3s ease;flex-shrink:0}
.dn-toggle--on{background:var(--terra)}
.dn-toggle__knob{position:absolute;top:3px;left:3px;width:18px;height:18px;border-radius:50%;background:var(--white);box-shadow:0 1px 4px rgba(0,0,0,.2);transition:transform .3s ease}
.dn-toggle--on .dn-toggle__knob{transform:translateX(20px)}
.dn-personal-form{display:flex;flex-direction:column;gap:1.1rem}
.dn-form-row{display:grid;grid-template-columns:1fr 1fr;gap:1rem}
@media(max-width:540px){.dn-form-row{grid-template-columns:1fr}}

/* ── Paiement ────────────────────────────────────────────── */
.dn-payment-opts{display:flex;flex-direction:column;gap:.75rem;margin-bottom:1.5rem}
.dn-pay-opt{display:flex;align-items:center;gap:1rem;padding:1.1rem 1.25rem;background:var(--white);border:1.5px solid var(--ink-12);border-radius:8px;cursor:pointer;text-align:left;transition:all .2s ease}
.dn-pay-opt:hover{border-color:var(--terra)}
.dn-pay-opt--active{border-color:var(--terra);background:rgba(200,90,42,.05)}
.dn-pay-opt__icon{width:44px;height:44px;border-radius:8px;background:var(--cream-d);display:flex;align-items:center;justify-content:center;flex-shrink:0}
.dn-pay-opt__icon :deep(svg){width:22px;height:22px;color:var(--terra)}
.dn-pay-opt__info{flex:1}
.dn-pay-opt__info p{font-size:.92rem;font-weight:500;color:var(--ink)}
.dn-pay-opt__info small{font-size:.78rem;color:var(--ink-60)}
.dn-pay-opt__check{width:20px;height:20px;color:var(--terra);flex-shrink:0}

.dn-payment-form{background:var(--cream);border-radius:8px;padding:1.5rem;margin-top:.75rem}
.dn-payment-form__title{font-family:var(--fd);font-size:1.1rem;font-weight:600;color:var(--ink);margin-bottom:.75rem}
.dn-payment-form__notice{display:flex;align-items:center;gap:.45rem;font-size:.8rem;color:var(--sage);margin-bottom:1rem}
.dn-payment-form__notice :deep(svg){width:14px;height:14px;flex-shrink:0}
.dn-stripe-element{border:1.5px solid var(--ink-12);border-radius:6px;padding:1rem;min-height:60px;background:var(--white)}
.dn-stripe-placeholder{display:flex;align-items:center;gap:.75rem;color:var(--ink-60);font-size:.82rem}
.dn-stripe-placeholder :deep(svg){width:20px;height:20px;flex-shrink:0}
.dn-operator-grid{display:flex;gap:.5rem;flex-wrap:wrap;margin-top:.85rem}
.dn-operator-btn{padding:.5rem 1rem;border-radius:4px;border:1.5px solid var(--ink-12);background:var(--white);font-size:.82rem;cursor:pointer;transition:all .2s ease}
.dn-operator-btn:hover,.dn-operator-btn--active{border-color:var(--terra);background:rgba(200,90,42,.06);color:var(--terra);font-weight:500}

/* ── Confirmation ────────────────────────────────────────── */
.dn-confirm{text-align:center}
.dn-confirm__icon{width:70px;height:70px;border-radius:50%;background:rgba(122,158,135,.15);display:flex;align-items:center;justify-content:center;margin:0 auto 1.5rem}
.dn-confirm__icon :deep(svg){width:36px;height:36px;color:var(--sage)}
.dn-confirm__details{background:var(--cream);border-radius:8px;padding:1.25rem;margin:1.5rem 0;text-align:left}
.dn-confirm-row{display:flex;justify-content:space-between;align-items:center;padding:.45rem 0;border-bottom:1px solid var(--ink-12);font-size:.88rem}
.dn-confirm-row:last-child{border-bottom:none}
.dn-confirm-row span{color:var(--ink-60)}
.dn-confirm-row strong{color:var(--ink)}
.dn-status{color:var(--amber)}
.dn-mobile-wait{display:flex;align-items:flex-start;gap:.75rem;background:rgba(232,147,58,.1);border-radius:8px;padding:1rem;margin-bottom:1rem;text-align:left}
.dn-mobile-wait :deep(svg){width:20px;height:20px;color:var(--amber);flex-shrink:0;margin-top:2px}
.dn-mobile-wait p{font-size:.87rem;color:var(--ink);line-height:1.55}
.dn-confirm__email{font-size:.83rem;color:var(--ink-60)}

/* ── Sidebar ─────────────────────────────────────────────── */
.dn-sidebar{display:flex;flex-direction:column;gap:1.25rem;position:sticky;top:80px}
.dn-summary{background:var(--white);border-radius:8px;padding:1.5rem;box-shadow:var(--shadow-sm)}
.dn-summary__title{font-family:var(--fd);font-size:1.15rem;font-weight:600;color:var(--ink);margin-bottom:1rem}
.dn-summary__rows{display:flex;flex-direction:column;gap:.45rem}
.dn-summary-row{display:flex;justify-content:space-between;align-items:center;font-size:.84rem;padding:.4rem 0;border-bottom:1px solid var(--ink-12)}
.dn-summary-row:last-child{border-bottom:none}
.dn-summary-row span{color:var(--ink-60)}
.dn-summary-row strong{color:var(--ink);font-weight:500;text-align:right;max-width:55%}
.dn-summary__fiscal{display:flex;align-items:flex-start;gap:.5rem;margin-top:1rem;padding-top:1rem;border-top:1px solid var(--ink-12);font-size:.8rem;color:var(--ink-60)}
.dn-summary__fiscal :deep(svg){width:14px;height:14px;color:var(--sage);flex-shrink:0;margin-top:2px}
.dn-impact-card{background:var(--white);border-radius:8px;padding:1.5rem;box-shadow:var(--shadow-sm);border-left:3px solid var(--terra)}
.dn-impact-card h3{font-family:var(--fd);font-size:1rem;font-weight:600;color:var(--ink);margin-bottom:.85rem}
.dn-impact-rows{display:flex;flex-direction:column;gap:.5rem}
.dn-impact-row{display:flex;align-items:center;gap:.6rem;font-size:.82rem;color:var(--ink-60);padding:.35rem 0;border-bottom:1px solid var(--cream-d)}
.dn-impact-row:last-child{border-bottom:none}
.dn-impact-row--highlight{color:var(--forest);font-weight:500}
.dn-impact-row__amt{font-family:var(--fd);font-size:1rem;font-weight:700;color:var(--terra);width:50px;flex-shrink:0}

/* ── Don PayPal ──────────────────────────────────────────── */
.dn-paypal-wrap{display:grid;grid-template-columns:1fr 320px;gap:2.5rem;align-items:start;max-width:900px;margin:0 auto}
@media(max-width:900px){.dn-paypal-wrap{grid-template-columns:1fr}}
.dn-paypal-card{}
.dn-paypal-notice{display:flex;align-items:center;gap:.5rem;font-size:.82rem;color:var(--ink-60);background:var(--cream);border-radius:8px;padding:.85rem 1rem;margin:1.5rem 0}
.dn-paypal-notice :deep(svg){width:16px;height:16px;color:var(--sage);flex-shrink:0}
.dn-paypal-btn{width:100%;justify-content:center}
.dn-paypal-alt{font-size:.8rem;color:var(--ink-60);text-align:center;margin-top:1rem}
.dn-paypal-alt a{color:var(--terra);font-weight:500;text-decoration:underline}
.dn-paypal-impact{}

/* ── Dons désactivés ─────────────────────────────────────── */
.dn-disabled{background:var(--white);border-radius:8px;padding:3rem 2rem;text-align:center;box-shadow:var(--shadow-sm);max-width:560px;margin:0 auto}
.dn-disabled__icon{width:64px;height:64px;border-radius:50%;background:var(--cream-d);display:flex;align-items:center;justify-content:center;margin:0 auto 1.5rem}
.dn-disabled__icon :deep(svg){width:32px;height:32px;color:var(--ink-60)}
.dn-disabled p{font-size:.92rem;color:var(--ink-60);line-height:1.65}
</style>